import { Box } from '@material-ui/core'
import { DynamicForm as DynamicFormType, Maybe, Scalars } from '@ts/operations'
import React from 'react'

import { useAnalytics } from '../../lib/analytics'
import { Actions, Categories } from '../../lib/analytics/events'
import { useScrollToRef } from '../../lib/hooks/useScrollToRef'
import { objectFrom } from '../../lib/json'
import { useSiteConfig } from '../../lib/siteconfig'
import { isBrowser } from '../../lib/ssrMode'
import { useAttributionParams } from '../../lib/tracking/attribution'
import SecondaryButton from '../button/SecondaryButton'
import Markdown from '../utils/Markdown'
import { Body2 } from '../utils/typography'
import DynamicForm from './DynamicForm'

export type DynamicFormstackFormProps = {
  contentBefore?: Maybe<Scalars['Markdown']>
  dynamicForm: DynamicFormType
  formAnalyticsGoal?: Maybe<string>
  formEventLabel?: Maybe<string>
  submitText?: string
}

const DEFAULT_SUCCESS_MESSAGE = '## Thanks for your submission'

const DynamicFormstackForm = ({
  dynamicForm,
  contentBefore,
  formAnalyticsGoal,
  formEventLabel,
  submitText = 'Submit',
}: DynamicFormstackFormProps) => {
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)
  const { htmlElRef, scrollToElement } = useScrollToRef({ behavior: 'smooth', block: 'center' })
  const params = useAttributionParams()

  const attributionFields = {
    campaign_content: params.utm_content,
    campaign_medium: params.utm_medium,
    campaign_name: params.utm_campaign,
    campaign_source: params.utm_source,
    campaign_term: params.utm_term,
  }
  const { googleAnalyticsTrackingId } = useSiteConfig()
  const { trackGoal, trackEvent, trackTagManagerEvent, user } = useAnalytics()
  const referrer = isBrowser ? window.location.href : ''
  const analyticsData = {
    ...attributionFields,
    gaclientid: user().clientId,
    gatrackid: googleAnalyticsTrackingId,
    referrer,
  }

  const handleSubmitted = ({ message }: { message?: Maybe<string> }) => {
    setSuccessMessage(message ?? DEFAULT_SUCCESS_MESSAGE)
    scrollToElement()
    trackEvent({
      action: Actions.Submit,
      category: `${Categories.Form} ${dynamicForm.id}`,
      location: `${Categories.Form} ${dynamicForm.id}`,
    })
    trackTagManagerEvent({
      action: Actions.FormSubmit,
      category: `${Categories.Form} ${dynamicForm.id}`,
      label: submitText,
      location: `${Categories.Form} ${dynamicForm.id}`,
    })
    trackGoal({ formAnalyticsGoal })
    if (formEventLabel) window.dataLayer.push({ formEventLabel })
  }

  const [isFirstInteraction, setIsFirstInteraction] = React.useState<boolean>(true)

  const handleFocus = (formElement: string) => {
    trackTagManagerEvent({
      action: isFirstInteraction ? Actions.FormFirstInteractionClick : Actions.FormInteractionClick,
      category: `${Categories.Form} ${dynamicForm.id}`,
      label: formElement,
      location: `${Categories.Form} ${dynamicForm.id}`,
    })
    if (isFirstInteraction) setIsFirstInteraction(false)
  }

  const { data, ...formProps } = dynamicForm
  return (
    <div ref={htmlElRef}>
      {successMessage ? (
        <Markdown renderers={{ paragraph: Body2 }} source={successMessage} />
      ) : (
        <>
          {contentBefore && <Markdown source={contentBefore} />}

          <DynamicForm
            {...formProps}
            data={objectFrom(data)}
            transformInput={input => ({ ...input, analyticsData })}
            onFocus={handleFocus}
            onSuccess={handleSubmitted}
          >
            <Box mt={4}>
              <SecondaryButton type="submit">{submitText}</SecondaryButton>
            </Box>
          </DynamicForm>
        </>
      )}
    </div>
  )
}

export default DynamicFormstackForm
